import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import Navigation from './Navigation';
import Home from './Home';
import PlayerSearch from './PlayerSearch';
import FitAnalysis from './components/FitAnalysis';
import Criteria from './Criteria';
import RecruitBoard from './DraftBoard';
import AcademicTracking from './AcademicTracking';
import UserManagement from './UserManagement';
import SchoolManagement from './SchoolManagement';
import ChangePassword from './ChangePassword';
import Login from './Login';
import TrialLogin from './TrialLogin';
import Signup from './Signup';
import SignupSuccess from './SignupSuccess';
import PublicProfile from './PublicProfile';
import AthletePortal from './athlete';
import RecruitingCenter from './recruiting/RecruitingCenter';
import AthleteSignIn from './recruiting/AthleteSignIn';
import Listings from './Listings';
/**
 * Route guard. The API already rejects the wrong role, but without this a coach
 * who typed the URL would still load the operator's page shell and see its
 * headings before the requests failed.
 */
const RequireRole = ({ roles, user, children }) =>
  roles.includes(user?.role) ? children : <Navigate to="/" replace />;

const ComingSoon = ({ title }) => (
  <div className="min-h-screen bg-[#0A0E27] pt-8 md:pt-12">
    <div className="max-w-7xl mx-auto px-4 md:px-6">
      <div className="panel-card text-center py-20">
        <h1 className="text-white text-3xl font-bold mb-4">{title}</h1>
        <p className="text-[#B0BEC5] text-lg">Coming Soon</p>
      </div>
    </div>
  </div>
);
function AppContent() {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Public recruiting profiles render before any auth check, with no app shell
  // and no navigation. A college coach opening a shared link must never be
  // asked to log in, and must never see another school's data.
  if (location.pathname.startsWith('/p/')) {
    return (
      <Routes>
        <Route path="/p/:token" element={<PublicProfile />} />
      </Routes>
    );
  }

  // Athlete sign-in is always reachable at its own address, even in a browser
  // where someone else is signed in; signing in there replaces that session.
  if (location.pathname === '/recruiting-login') {
    return <AthleteSignIn />;
  }

  // Guardian portal: its own auth, its own shell, no coach navigation.
  if (location.pathname.startsWith('/athlete')) {
    return (
      <Routes>
        <Route path="/athlete/*" element={<AthletePortal />} />
      </Routes>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-blue-400 text-xl">Loading...</div>
      </div>
    );
  }
  if (!user) {
    return (
      <Routes>
        <Route path="/trial-login" element={<TrialLogin />} />
        <Route path="/recruiting-login" element={<AthleteSignIn />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-success" element={<SignupSuccess />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }
  if (user.role === 'guardian') {
    return <Navigate to="/athlete" replace />;
  }
  // An athlete account exists only for the Recruiting Center. It must never
  // fall through to the coaching app below.
  if (user.role === 'athlete') {
    return <RecruitingCenter standalone />;
  }

  return (
    <div className="min-h-screen bg-[#0A0E27] has-tab-bar">
      <Navigation />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/players" element={<PlayerSearch />} />
        <Route path="/players/:id/fit" element={<FitAnalysis />} />
        <Route path="/criteria" element={<Criteria />} />
        <Route path="/recruit-board" element={<RecruitBoard />} />
        <Route path="/academic-tracking" element={<AcademicTracking />} />
        <Route path="/users" element={<UserManagement />} />
        <Route path="/schools" element={<SchoolManagement />} />
        <Route
          path="/billing"
          element={
            <RequireRole roles={['super_admin']} user={user}>
              <Listings />
            </RequireRole>
          }
        />
        <Route
          path="/recruiting-center"
          element={
            <RequireRole roles={['super_admin']} user={user}>
              <RecruitingCenter />
            </RequireRole>
          }
        />
        <Route path="/change-password" element={<ChangePassword />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/signup-success" element={<SignupSuccess />} />
        <Route path="/grading" element={<ComingSoon title="Player Grading" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}
export default App;
